import { useState, useEffect, useCallback, useRef } from "react";
import { useHistory } from "react-router-dom";
import { has, isArray } from "lodash";

import { useToast } from "../../hooks/useToast";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { socketConnection } from "../../services/socket";
import { computeOwnUserUpdate } from "./ownUserUpdate";
import {
  MENU_PERMISSION_REFRESH_DELAY_MS,
  createMenuPermissionRefreshScheduler,
  shouldRefreshMenuPermissions,
} from "./menuPermissionRefresh";
import observerReadService from "../../services/observerReadService";
import dayjs from "../../utils/dayjs";
import "dayjs/locale/pt-br";
import { formatDate } from "../../utils/dateUtils";

let interceptorsConfigured = false;
let isRefreshing = false;
let refreshSubscribers = [];
// 🔒 Flag idempotente: quando MUITAS chamadas paralelas tomam 402 ao mesmo
// tempo (ex.: empresa desativada com LoggedInLayout já montado), precisamos
// disparar setState/history.replace UMA única vez. Sem isto, o loop de
// notifyListeners do react-router gera React Error #185 (Maximum update depth
// exceeded) e a tela inteira fica branca.
let subscriptionExpiredHandled = false;

const useAuth = () => {
  const history = useHistory();

  const toast = useToast();
  const [isAuth, setIsAuth] = useState(false);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState({});
  const [subscriptionExpired, setSubscriptionExpired] = useState(false);
  const [socket, setSocket] = useState(null);
  const userRef = useRef(user);
  const menuPermissionRequestSeq = useRef(0);

  useEffect(() => {
    userRef.current = user;
  });

  // ✅ Helper para limpar TODOS os dados de autenticação do localStorage
  // Nota: setIsAuth e setUser são funções de setState do React que são estáveis,
  // então não precisam estar nas dependências
  const clearAuthData = useCallback(() => {
    localStorage.removeItem("token");
    localStorage.removeItem("companyId");
    localStorage.removeItem("userId");
    localStorage.removeItem("cshow");
    localStorage.removeItem("companyDueDate");
    localStorage.removeItem("sendSignMessage");
    localStorage.removeItem("profileImage");
    localStorage.removeItem("companyTimezone"); // 🌍 Limpar timezone
    api.defaults.headers.Authorization = undefined;
    setIsAuth(false);
    setUser({});
    setSubscriptionExpired(false);
    // 🔓 Resetar flag idempotente para que um próximo login com empresa bloqueada
    // volte a disparar o redirect uma vez.
    subscriptionExpiredHandled = false;
    // 🔇 Mute local de tickets visualizados como observador — resetar sessão
    // ao deslogar, preservando o localStorage para o mesmo usuário/empresa
    // voltar com seu estado visual local.
    try {
      observerReadService.clear();
    } catch {
      // ignore
    }
  }, []); // setIsAuth e setUser são estáveis (não mudam)

  useEffect(() => {
    if (interceptorsConfigured) return;

    const reqId = api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem("token");
        if (token) {
          let parsedToken = token;
          try {
            parsedToken = JSON.parse(token);
          } catch {
            parsedToken = token;
          }
          config.headers["Authorization"] = `Bearer ${parsedToken}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    const resId = api.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config || {};

        // 🔒 Bloqueio por assinatura (server-side gating)
        // Não tentar refresh em 402/423; apenas redirecionar para /expired-subscription.
        const statusCode = error?.response?.status;
        const errorCode = error?.response?.data?.error;
        if (
          (statusCode === 402 || statusCode === 423) &&
          errorCode === "ERR_SUBSCRIPTION_PAYMENT_REQUIRED"
        ) {
          const dueDateFormatted = error?.response?.data?.dueDateFormatted;
          if (dueDateFormatted) {
            localStorage.setItem("companyDueDate", dueDateFormatted);
          }
          // ✅ Idempotente: dispara setState + redirect apenas na PRIMEIRA 402
          // recebida. Chamadas subsequentes apenas rejeitam silenciosamente.
          // Isto evita o loop de re-render quando ~10 requests paralelas falham
          // ao mesmo tempo (vide MainListItems, sidebars, push, etc.).
          if (!subscriptionExpiredHandled) {
            subscriptionExpiredHandled = true;
            setSubscriptionExpired(true);
            setIsAuth(false);
            if (history.location.pathname !== "/expired-subscription") {
              history.replace("/expired-subscription");
            }
          }
          return Promise.reject(error);
        }
        
        // 🚀 CORREÇÃO: Tentar refresh tanto no 401 quanto no 403
        if (
          (error?.response?.status === 401 || error?.response?.status === 403) && 
          !originalRequest._retry
        ) {
          // Não tentar refresh em erros de mídia/proxy/login/refresh
          const isMediaError = originalRequest.url?.includes('/proxy-media/') || 
                              originalRequest.url?.includes('/public/') ||
                              originalRequest.url?.includes('lookaside.fbsbx.com') ||
                              originalRequest.url?.includes('google.com');
          
          const isTestConnection = originalRequest.url?.includes('/test-waba');
          const isRefreshToken = originalRequest.url?.includes('/auth/refresh_token');
          const isLoginRequest = originalRequest.url?.includes('/auth/login');
          const isSignupRequest = originalRequest.url?.includes('/auth/signup');
          
          // ✅ CORREÇÃO CRÍTICA: Incluir isRefreshToken na verificação para evitar loop infinito
          if (isMediaError || isTestConnection || isLoginRequest || isSignupRequest || isRefreshToken) {
            // ✅ Se refresh_token falhou com 401, fazer logout imediato
            if (isRefreshToken) {
              console.log('❌ [useAuth] Refresh token expirado - redirecionando para login');
              clearAuthData();
              // Se o usuário já está na tela de renovação, não forçar navegação para login
              if (
                history.location.pathname !== "/expired-subscription" &&
                history.location.pathname !== "/login" &&
                history.location.pathname !== "/signup"
              ) {
                history.push("/login");
              }
            }
            return Promise.reject(error);
          }
          
          // ✅ PROTEÇÃO: Não tentar refresh se já estiver em processo
          if (isRefreshing) {
            // Aguardar o refresh em andamento
            return new Promise((resolve, reject) => {
              refreshSubscribers.push((token) => {
                if (token) {
                  originalRequest.headers.Authorization = `Bearer ${token}`;
                  resolve(api(originalRequest));
                } else {
                  reject(error);
                }
              });
            });
          }
          
          originalRequest._retry = true;
          isRefreshing = true;
          
          try {
            console.log('🔄 [useAuth] Tentando refresh token...');
            const { data } = await api.post("/auth/refresh_token");
            
            if (data && data.token) {
              console.log('✅ [useAuth] Refresh token bem-sucedido');
              const newToken = data.token;
              localStorage.setItem("token", JSON.stringify(newToken));
              api.defaults.headers.Authorization = `Bearer ${newToken}`;
              setIsAuth(true);
              
              // ✅ Notificar todos os subscribers que o token foi atualizado
              refreshSubscribers.forEach(cb => cb(newToken));
              refreshSubscribers = [];
              
              // ✅ Retenta o request original com novo token
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
              return api(originalRequest);
            }
          } catch (refreshError) {
            console.error('❌ [useAuth] Refresh token falhou:', refreshError);
            
            // ✅ Notificar todos os subscribers que o refresh falhou
            refreshSubscribers.forEach(cb => cb(null));
            refreshSubscribers = [];
            
            // ❌ Refresh falhou → Limpar TODOS os dados de autenticação
            clearAuthData();
            
            // Mostrar toast e redirecionar (se não for request de refresh inicial)
            if (!isRefreshToken) {
              toast.error("Sessão expirada. Faça login novamente.");
              if (history.location.pathname !== '/login' && history.location.pathname !== '/signup') {
                history.push("/login");
              }
            }
            
            return Promise.reject(refreshError);
          } finally {
            isRefreshing = false;
          }
        }
        
        return Promise.reject(error);
      }
    );

    interceptorsConfigured = true;

    return () => {
      api.interceptors.request.eject(reqId);
      api.interceptors.response.eject(resId);
      interceptorsConfigured = false;
    };
  }, [toast, history, clearAuthData]); // ✅ Adicionado clearAuthData

  useEffect(() => {
    const token = localStorage.getItem("token");
    (async () => {
      if (token) {
        try {
          const { data } = await api.post("/auth/refresh_token");
          api.defaults.headers.Authorization = `Bearer ${data.token}`;
          localStorage.setItem("token", JSON.stringify(data.token));

          // Verificar assinatura ANTES de marcar como autenticado
          let expired = false;
          if (data.user?.company?.id && data.user.company.id !== 1) {
            const dueDate = dayjs(data.user.company.dueDate);
            // Considerar fim do dia para evitar bloqueio prematuro por timezone/horário
            const dueEnd = dueDate.isValid() ? dueDate.endOf("day") : dueDate;
            const dueDateExpired = dueEnd.isValid() && dayjs().isAfter(dueEnd);

            // 🔒 Espelhar gating do backend: empresa desativada (status=false) ou
            // status de assinatura bloqueante também precisam ir direto para
            // /expired-subscription, sem montar o LoggedInLayout (evita storm 402).
            const subStatus = String(data.user.company.subscriptionStatus || "").toLowerCase();
            const blockedStatuses = ["pending_payment", "overdue", "blocked", "cancelled"];
            const companyDisabled =
              data.user.company.status === false ||
              blockedStatuses.includes(subStatus);

            expired = dueDateExpired || companyDisabled;

            if (expired) {
              const baseDate = dueEnd.isValid() ? dueEnd : dueDate;
              if (baseDate.isValid()) {
                const vencimento = formatDate(baseDate.toDate());
                localStorage.setItem("companyDueDate", vencimento);
              }
            }
          }

          setUser(data.user);
          setSubscriptionExpired(!!expired);

          // 🌍 Salvar timezone da empresa no localStorage
          if (data.user?.timezone) {
            localStorage.setItem("companyTimezone", data.user.timezone);
          }

          if (expired) {
            // Não renderizar o app logado; direcionar para a tela de renovação
            // 🔒 Marcar flag idempotente: requests em paralelo (push, sockets,
            // refresh inicial de outros componentes) podem retornar 402 logo
            // após este ponto. Sem a flag, cada um faria setState/replace.
            subscriptionExpiredHandled = true;
            setIsAuth(false);
            if (history.location.pathname !== "/expired-subscription") {
              history.replace("/expired-subscription");
            }
            return;
          }

          setIsAuth(true);
        } catch (err) {
          // ✅ CORREÇÃO: Se refresh falhar, limpar TODOS os dados e redirecionar para login
          console.error('❌ [useAuth] Token inválido/expirado na inicialização:', err);
          clearAuthData();
          
          // Só mostrar toast se não for erro de rede (para não poluir com erros técnicos)
          if (err?.response?.status === 401 || err?.response?.status === 403) {
            toast.info("Sessão expirada. Por favor, faça login novamente.");
          } else {
            toastError(err, toast);
          }
          
          // Redirecionar para login (se não estiver já lá)
          // Se o usuário está em /expired-subscription, não forçar redirect automático.
          if (
            history.location.pathname !== "/expired-subscription" &&
            history.location.pathname !== "/login" &&
            history.location.pathname !== "/signup"
          ) {
            history.push("/login");
          }
        }
      }
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // ✅ Executar apenas uma vez na montagem. 
          // clearAuthData, toast, history estão disponíveis na primeira montagem
          // e não precisam estar nas dependências pois só roda uma vez

  // 🔇 Inicializar mute local de tickets visualizados como observador
  // sempre que (re)conhecermos o usuário/empresa. Reage a refresh, login ou
  // troca de empresa (cada combinação tem seu próprio Set isolado).
  useEffect(() => {
    if (user?.id && user?.companyId) {
      try {
        observerReadService.init(user.id, user.companyId);
      } catch {
        // ignore — service falha silencioso se localStorage indisponível
      }
    }
  }, [user?.id, user?.companyId]);

  useEffect(() => {
    if (user && user.id > 0) {
      let io;
      if (!socket) {
        io = socketConnection({ user });
        setSocket(io);
      } else {
        io = socket;
      }

      if (io && typeof io.on === 'function') {
      io.on(`company-${user.companyId}-user`, (data) => {
        const update = computeOwnUserUpdate(user, data);
        if (!update) return;

        setUser((prevUser) => ({
          ...prevUser,
          ...update.patch,
        }));

        if (update.permissionsChanged) {
          toast.info(
            i18n.t("Suas permissões foram atualizadas pelo administrador.")
          );
        }
      });

      return () => {
        io.off(`company-${user.companyId}-user`);
      };
    }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.id, user.companyId, socket]);

  useEffect(() => {
    if (!socket || !user?.id || !(Number(user.id) > 0)) return undefined;
    if (typeof socket.on !== "function") return undefined;

    // Contador, não nó do DOM: a limpeza precisa incrementar o valor ATUAL para
    // invalidar um GET /auth/me ainda em voo. Guardar o objeto do ref (estável)
    // mantém essa semântica; copiar `.current` a quebraria.
    const requestSeq = menuPermissionRequestSeq;
    const scheduler = createMenuPermissionRefreshScheduler({
      delayMs: MENU_PERMISSION_REFRESH_DELAY_MS,
      onFire: (roles) => {
        const current = userRef.current;
        if (!shouldRefreshMenuPermissions(current, roles)) return;
        const seq = ++requestSeq.current;
        api
          .get("/auth/me")
          .then(({ data }) => {
            if (seq !== requestSeq.current) return;
            if (!data || !Array.isArray(data.menuPermissions)) return;
            setUser((prev) => ({
              ...prev,
              menuPermissions: data.menuPermissions,
            }));
          })
          .catch(() => {});
      },
    });

    const eventName = `company-${user.companyId}-menu-permissions`;
    const onMenuPermissions = (data) => {
      if (!data || data.action !== "update" || !Array.isArray(data.roles)) {
        return;
      }
      scheduler.push(data.roles.filter((role) => typeof role === "string"));
    };

    socket.on(eventName, onMenuPermissions);
    return () => {
      if (typeof socket.off === "function") {
        socket.off(eventName, onMenuPermissions);
      }
      scheduler.cancel();
      ++requestSeq.current;
    };
  }, [socket, user?.id, user?.companyId]);

  const handleLogin = async (userData) => {
    setLoading(true);
    // 🔓 Resetar flag idempotente do interceptor 402 a cada novo login.
    subscriptionExpiredHandled = false;

    try {
      const { data } = await api.post("/auth/login", userData);
      const {
        user: { company },
      } = data;

      if (
        company && typeof company === 'object' &&
        has(company, "companieSettings") &&
        isArray(company.companieSettings[0])
      ) {
        const setting = company.companieSettings[0].find(
          (s) => s.key === "campaignsEnabled"
        );
        if (setting && setting.value === "true") {
          localStorage.setItem("cshow", null);
        }
      }

      if (
        company && typeof company === 'object' &&
        has(company, "companieSettings") &&
        isArray(company.companieSettings[0])
      ) {
        const setting = company.companieSettings[0].find(
          (s) => s.key === "sendSignMessage"
        );

        const signEnable = setting.value === "enable";

        if (setting && setting.value === "enabled") {
          localStorage.setItem("sendSignMessage", signEnable);
        }
      }
      localStorage.setItem("profileImage", data.user.profileImage);

      dayjs.locale("pt-br");
      let dueDate;
      if (data.user.company.id === 1) {
        dueDate = "2999-12-31T00:00:00.000Z";
      } else {
        dueDate = data.user.company.dueDate;
      }
      const dueDateInstance = dayjs(dueDate);
      const vencimento = formatDate(dueDateInstance.toDate());

      const now = dayjs();
      const diff = dueDateInstance.diff(now);

      // Considerar fim do dia para evitar bloqueio prematuro por timezone/horário
      const dueEnd = dueDateInstance.isValid() ? dueDateInstance.endOf("day") : dueDateInstance;
      const before = now.isBefore(dueEnd);
      // Usar ceil para arredondar para cima (mais amigável para o usuário)
      // Ex: 2.5 dias → 3 dias (não 2)
      const dias = Math.ceil(dayjs.duration(diff).asDays());

      // 🔒 Bloqueio adicional: empresa desativada manualmente (status=false) ou
      // status de assinatura bloqueante. Espelha o gating server-side
      // (backend/src/middleware/ensureCompanyAccess.ts) para evitar storm de
      // chamadas paralelas e loop de re-render quando a tela logada monta.
      const subStatus = String(company?.subscriptionStatus || "").toLowerCase();
      const blockedStatuses = ["pending_payment", "overdue", "blocked", "cancelled"];
      const companyDisabled =
        company?.id !== 1 &&
        (company?.status === false || blockedStatuses.includes(subStatus));

      if (before === true && !companyDisabled) {
        localStorage.setItem("token", JSON.stringify(data.token));
        localStorage.setItem("companyDueDate", vencimento);
        api.defaults.headers.Authorization = `Bearer ${data.token}`;
        setUser(data.user);
        setSubscriptionExpired(false);
        setIsAuth(true);

        // 🌍 Salvar timezone da empresa no localStorage
        if (data.user?.timezone) {
          localStorage.setItem("companyTimezone", data.user.timezone);
        }

        toast.success(i18n.t("auth.toasts.success"));
        
        // ✅ Aviso específico para modo TRIAL - só mostrar se for trial válido
        // Verificar: NÃO é empresa master (ID 1) E subscriptionStatus === "trial" E trialEndsAt existe E está no futuro (dentro de 30 dias)
        const trialEndDate = company.trialEndsAt ? dayjs(company.trialEndsAt) : null;
        const isValidTrial = company.id !== 1 // Empresa master não tem trial
          && company.subscriptionStatus === "trial" 
          && trialEndDate 
          && trialEndDate.isValid()
          && trialEndDate.isAfter(now) // Trial ainda não expirou
          && trialEndDate.diff(now, "day") <= 30; // E está dentro de 30 dias (evita empresas antigas)
        
        if (isValidTrial) {
          const trialDiff = trialEndDate.diff(now);
          const trialDias = Math.ceil(dayjs.duration(trialDiff).asDays());
          
          // Garantir que temos pelo menos 1 dia (arredondamento)
          if (trialDias >= 1) {
            toast.info(
              `🎁 Você está em período de avaliação.\n${trialDias === 1 ? "Resta 1 dia" : `Restam ${trialDias} dias`} para assinar.`,
              { duration: 8000, centered: true }
            );
          }
        } else if (dias < 5 && dias > 0) {
          // Aviso para assinaturas normais próximas do vencimento
          toast.warning(
            `Sua assinatura vence em ${dias} ${dias === 1 ? "dia" : "dias"}`
          );
        }

        history.push("/tickets");
        setLoading(false);
      } else {
        // Assinatura vencida ou empresa desativada - direcionar para renovação
        localStorage.setItem("token", JSON.stringify(data.token));
        localStorage.setItem("companyDueDate", vencimento);
        api.defaults.headers.Authorization = `Bearer ${data.token}`;

        // 🔒 Já marcar flag para que qualquer 402 que chegue de chamadas
        // disparadas em paralelo durante a transição não faça setState extra.
        subscriptionExpiredHandled = true;

        setUser(data.user);
        setSubscriptionExpired(true);
        setIsAuth(false);
        setLoading(false);
        history.replace("/expired-subscription");
        return;
      }
    } catch (err) {
      toastError(err, toast);
      setLoading(false);
      // Propagar erro para o componente Login poder exibir na UI
      throw err;
    }
  };

  const handleLogout = async () => {
    setLoading(true);

    try {
      await api.delete("/auth/logout");
      clearAuthData(); // ✅ Limpar TODOS os dados de autenticação
      setLoading(false);
      history.push("/login");
    } catch (err) {
      toastError(err, toast);
      setLoading(false);
    }
  };

  const getCurrentUserInfo = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      return data;
    } catch (_) {
      return null;
    }
  }, []);

  return {
    isAuth,
    user,
    loading,
    subscriptionExpired,
    handleLogin,
    handleLogout,
    getCurrentUserInfo,
    socket,
  };
};

export default useAuth;
